import React from "react";
import {ArrowTopRightOnSquareIcon, PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import SimmsInput from "../../../../../components/input/SimmsInput";
import SimmsInputSearch from "../../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../../components/drawer/simms/SimmsDrawerSearch";
import Selector from "../../../../../components/selector/Selector";
import {CodeSpecNum} from "../../../../../constant/CodeSpecNum";
import "./licenseKeyEditor.css";

function getAsset(item) {
    if (!item?.asset) return null;
    const raw = Array.isArray(item.asset) ? item.asset[0] : item.asset;
    return raw && Object.keys(raw).length > 0 ? raw : null;
}

function getKeyValue(item) {
    return item?.licenseKey ?? item?.key ?? "";
}

/** 수량 — 1 이상 양의 정수만 허용 */
function sanitizePositiveQuantity(value, {allowEmpty = true} = {}) {
    if (value === "" || value == null) {
        return allowEmpty ? "" : null;
    }
    const digits = String(value).replace(/[^\d]/g, "");
    if (!digits) {
        return allowEmpty ? "" : null;
    }
    const num = parseInt(digits, 10);
    if (!Number.isFinite(num) || num <= 0) {
        return allowEmpty ? "" : null;
    }
    return String(num);
}

function AssetSearchFilters({platFrom}) {
    return (
        <>
            <SimmsInputSearch type="TEXT" placeHolder="자산명" />
            <SimmsDrawerSearch
                showLabel
                title="자산그룹"
                entity="code"
                target="group"
                parentKey={CodeSpecNum.AssetGroupCode}
                uniqueKey="group"
                recursive
            />
            <SimmsDrawerSearch
                showLabel
                title="업무그룹"
                entity="code"
                target="task"
                parentKey={CodeSpecNum.TaskGroupCode}
                uniqueKey="task"
                recursive
            />
            <SimmsDrawerSearch
                showLabel
                title="자산위치"
                entity="code"
                target="location"
                parentKey={CodeSpecNum.AssetLocation}
                uniqueKey="location"
                recursive
            />
            {platFrom === "ITM" && (
                <SimmsDrawerSearch
                    showLabel
                    title="SLA 그룹"
                    entity="code"
                    target="slaGroup"
                    parentKey={CodeSpecNum.SlaGroupCode}
                    uniqueKey="slaGroup"
                    recursive
                />
            )}
            <SimmsDrawerSearch showLabel title="운영담당자" entity="userAccount" target="operator" />
            <SimmsDrawerSearch showLabel title="자산사용자" entity="userAccount" target="handler" />
        </>
    );
}

function LicenseKeyRow({
    item,
    index,
    isEditing,
    fieldAuth,
    platFrom,
    onKeyChange,
    onKeyCommit,
    onAssetChange,
    onAssetNavigate,
    onRemove,
}) {
    const asset = getAsset(item);
    const keyValue = getKeyValue(item);
    const assetName = asset?.assetName ?? asset?.name;

    return (
        <li className="license-key-row">
            <span className="license-key-row__no" aria-hidden="true">
                {index + 1}
            </span>

            <div className="license-key-row__body">
                <div className="license-key-row__key">
                    {isEditing && fieldAuth ? (
                        <SimmsInput
                            auth={fieldAuth}
                            clicked
                            setClicked={() => {}}
                            update={Boolean(onKeyCommit)}
                            data={keyValue}
                            placeholder="라이선스 키 입력"
                            onChange={(v) => onKeyChange?.(index, v)}
                            onKeyDown={(value, event) => onKeyCommit?.(index, value, event, item)}
                        />
                    ) : (
                        <span className="license-key-row__readonly">{keyValue || "-"}</span>
                    )}
                </div>

                <div className="license-key-row__asset">
                    {isEditing && fieldAuth ? (
                        <Selector
                            auth={fieldAuth}
                            onChange={(v) => onAssetChange?.(index, v, item)}
                            data={asset}
                            title={`자산_${index}`}
                            placeHolder="자산 선택"
                            entity="asset"
                            uniqueKey={`licenseAsset-${item.id ?? index}`}
                        >
                            <AssetSearchFilters platFrom={platFrom} />
                        </Selector>
                    ) : assetName ? (
                        <span className="license-key-row__asset-name" title={assetName}>
                            {assetName}
                        </span>
                    ) : (
                        <span className="license-key-row__asset-empty">미연결</span>
                    )}
                    {asset?.id && onAssetNavigate && (
                        <button
                            type="button"
                            className="license-key-row__link-btn print-hidden"
                            title="자산 상세"
                            aria-label="자산 상세"
                            onClick={() => onAssetNavigate(asset)}
                        >
                            <ArrowTopRightOnSquareIcon />
                        </button>
                    )}
                </div>

                <div className="license-key-row__actions">
                    {isEditing && fieldAuth && (
                        <button
                            type="button"
                            className="license-key-row__remove-btn print-hidden"
                            title="삭제"
                            aria-label="삭제"
                            onClick={() => onRemove?.(item, index)}
                        >
                            <TrashIcon />
                            <span>삭제</span>
                        </button>
                    )}
                </div>
            </div>
        </li>
    );
}

export function LicenseKeyEditor({
    licenseKeys = [],
    quantity,
    onQuantityChange,
    onQuantityCommit,
    quantityUpdate = false,
    isEditing = false,
    fieldAuth = false,
    platFrom,
    onAdd,
    onRemove,
    onKeyChange,
    onKeyCommit,
    onAssetChange,
    onAssetNavigate,
}) {
    const maxQuantity = Number(quantity) || 0;
    const registeredCount = licenseKeys.length;
    const canEdit = isEditing && fieldAuth;
    const canAdd = canEdit && maxQuantity > 0 && registeredCount < maxQuantity;

    const handleQuantityChange = (v) => {
        onQuantityChange?.(sanitizePositiveQuantity(v));
    };

    const handleQuantityCommit = (v, e) => {
        const sanitized = sanitizePositiveQuantity(v, {allowEmpty: false});
        if (!sanitized) {
            alert("수량은 1 이상의 양수만 입력할 수 있습니다.");
            return;
        }
        onQuantityCommit?.(sanitized, e);
    };

    return (
        <div className="license-key-editor">
            <div className="license-key-editor__toolbar">
                <div className="license-key-editor__toolbar-left">
                    {canEdit ? (
                        <>
                            <div className="license-key-editor__quantity">
                                <span className="license-key-editor__quantity-label">수량 입력</span>
                                <SimmsInput
                                    auth={fieldAuth}
                                    clicked
                                    setClicked={() => {}}
                                    type="number"
                                    min={1}
                                    step={1}
                                    inputMode="numeric"
                                    update={quantityUpdate}
                                    data={quantity}
                                    constraint={{type: "number"}}
                                    placeholder="0"
                                    onChange={handleQuantityChange}
                                    onKeyDown={onQuantityCommit ? handleQuantityCommit : undefined}
                                    className="license-key-editor__quantity-input"
                                    style={{width: "72px"}}
                                />
                            </div>
                            <span className="license-key-editor__count">
                                등록 <strong>{registeredCount}</strong>건
                            </span>
                            <button
                                type="button"
                                className="license-key-editor__add-btn"
                                onClick={onAdd}
                                disabled={!canAdd}
                            >
                                <PlusIcon aria-hidden="true" />
                                키 추가
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="license-key-editor__quantity-readonly">
                                수량 입력 <strong>{quantity ?? 0}</strong>
                            </span>
                            <span className="license-key-editor__count">
                                등록 <strong>{registeredCount}</strong>건
                            </span>
                        </>
                    )}
                </div>
            </div>

            {registeredCount === 0 ? (
                <div className="license-key-board__empty">
                    {canEdit
                        ? maxQuantity > 0
                            ? "등록된 키가 없습니다. 「키 추가」로 등록하세요."
                            : "수량을 입력한 뒤 「키 추가」로 등록하세요."
                        : "등록된 키가 없습니다."}
                </div>
            ) : (
                <ul className="license-key-board__list">
                    {licenseKeys.map((item, index) => (
                            <LicenseKeyRow
                                key={item.id ?? `license-key-${index}`}
                                item={item}
                                index={index}
                                isEditing={isEditing}
                                fieldAuth={fieldAuth}
                                platFrom={platFrom}
                                onKeyChange={onKeyChange}
                                onKeyCommit={onKeyCommit}
                                onAssetChange={onAssetChange}
                                onAssetNavigate={onAssetNavigate}
                                onRemove={onRemove}
                            />
                        ))}
                </ul>
            )}
        </div>
    );
}
